import type { CSSProperties } from "react";
import { money } from "@/plan-logic/lib/format";
import {
  MoneyField,
  NumberField,
  RATE_UNKNOWN,
  RateField,
  SelectField,
  TextField,
  YesNo,
} from "@/components/fields";
import {
  DEBT_KIND_LABEL,
  type DraftDebt,
  type DraftProperty,
  PROPERTY_KIND_LABEL,
} from "@/plan-logic/questionnaire/draft";

/**
 * A property (with its mortgage) and a debt as onboarding's "What you owe" step and Update Data's
 * "What you owe" section both ask about them: the same questions, labels, order, and required
 * answers (`questionnaire/owed.ts` `validateOwed`). A mortgage is asked by the year it was taken out
 * and how many years it was for (v40), not the years left. Test ids are `<prefix>property-<i>-...` and
 * `<prefix>debt-<i>-...`; errors are looked up by `properties.<i>.<field>` and `debts.<i>.<field>`.
 */
type RowProps<T> = {
  row: T;
  i: number;
  onChange: (change: Partial<T>) => void;
  /** Omitted: no Remove button (onboarding's first row, which "Yes" asked for). */
  onRemove?: () => void;
  err: (path: string) => string | undefined;
  /** "ud-" in Update Data. */
  prefix?: string;
  /** Update Data's grid fields sit flush (margin 0). */
  fieldStyle?: CSSProperties;
  className?: string;
};

const label = <T,>(options: [T, string][], v: T, fallback: string) =>
  options.find(([k]) => k === v)?.[1] ?? fallback;

/**
 * A stored mortgage's terms when the year and the term aren't there (years left, from before v40,
 * or a payment): said in the hint, and kept as they are until both are given.
 */
function storedTerms(p: DraftProperty): string | undefined {
  if (p.startYear !== undefined || p.termYears !== undefined) return undefined;
  if (p.remainingMonths) {
    const years = Math.round(p.remainingMonths / 12);
    return `On file: about ${years} year${years === 1 ? "" : "s"} left to pay. Give the year you took it out and how many years it was for to replace that.`;
  }
  if (p.monthlyPayment)
    return `On file: ${money(p.monthlyPayment)} a month. Give the year you took it out and how many years it was for to replace that.`;
  return undefined;
}

export function propertyTitle(p: DraftProperty): string {
  return p.name.trim() || label(PROPERTY_KIND_LABEL, p.kind, "Property");
}

export function debtTitle(d: DraftDebt): string {
  return d.name.trim() || label(DEBT_KIND_LABEL, d.kind, "Debt");
}

function Head({ title, onRemove }: { title: string; onRemove?: () => void }) {
  return (
    <div className="rowhead">
      <b>{title}</b>
      {onRemove && (
        <button type="button" className="rm" onClick={onRemove}>
          Remove
        </button>
      )}
    </div>
  );
}

export function PropertyRow({
  row: p,
  i,
  onChange,
  onRemove,
  err,
  prefix = "",
  fieldStyle,
  className = "rowbox",
}: RowProps<DraftProperty>) {
  const id = `${prefix}property-${i}`;
  return (
    <div className={className} data-testid={id} data-mtg={p.hasMortgage ? "1" : "0"}>
      <Head title={propertyTitle(p)} onRemove={onRemove} />
      <div className="two">
        <SelectField
          label="What is it"
          value={p.kind}
          options={PROPERTY_KIND_LABEL}
          onChange={(kind) => onChange({ kind })}
          testId={`${id}-kind`}
          style={fieldStyle}
        />
        <TextField
          label="Name (optional)"
          value={p.name}
          placeholder="e.g. Lake house"
          onChange={(name) => onChange({ name })}
          testId={`${id}-name`}
          style={fieldStyle}
        />
      </div>
      <div className="two" style={fieldStyle && { marginTop: 12 }}>
        <MoneyField
          label="Current value"
          value={p.value}
          required
          onChange={(value) => onChange({ value })}
          error={err(`properties.${i}.value`)}
          testId={`${id}-value`}
          style={fieldStyle}
        />
        <MoneyField
          label="Property tax, a year (optional)"
          value={p.propertyTax}
          onChange={(propertyTax) => onChange({ propertyTax })}
          error={err(`properties.${i}.propertyTax`)}
          testId={`${id}-tax`}
          style={fieldStyle}
        />
      </div>
      <div className="mtgq tight">
        <span className="mq">Is there a mortgage on it?</span>
        <YesNo
          value={p.hasMortgage}
          onChange={(hasMortgage) => onChange({ hasMortgage })}
          label="Is there a mortgage on it?"
          testId={`${id}-has-mortgage`}
        />
      </div>
      {p.hasMortgage && (
        <div className="mtgfields">
          <div className="two">
            <MoneyField
              label="Amount still owing"
              value={p.owing}
              required
              onChange={(owing) => onChange({ owing })}
              error={err(`properties.${i}.owing`)}
              testId={`${id}-owing`}
              style={fieldStyle}
            />
            <RateField
              label="Interest rate"
              value={p.rate}
              required={false}
              placeholder={RATE_UNKNOWN}
              onChange={(rate) => onChange({ rate })}
              error={err(`properties.${i}.rate`)}
              testId={`${id}-rate`}
              style={fieldStyle}
            />
          </div>
          <div className="two" style={{ marginTop: 12 }}>
            <NumberField
              label="Year you took it out"
              value={p.startYear}
              onChange={(startYear) => onChange({ startYear })}
              error={err(`properties.${i}.startYear`)}
              placeholder="e.g. 2019"
              testId={`${id}-start`}
              style={fieldStyle}
            />
            <NumberField
              label="How many years it was for"
              value={p.termYears}
              onChange={(termYears) => onChange({ termYears })}
              error={err(`properties.${i}.termYears`)}
              placeholder="e.g. 30"
              testId={`${id}-term`}
              style={fieldStyle}
            />
          </div>
          <p className="hint" data-testid={`${id}-term-hint`}>
            {storedTerms(p) ?? "Abi works out what is left from those two."}
          </p>
        </div>
      )}
    </div>
  );
}

export function DebtRow({
  row: d,
  i,
  onChange,
  onRemove,
  err,
  prefix = "",
  fieldStyle,
  className = "rowbox",
}: RowProps<DraftDebt>) {
  const id = `${prefix}debt-${i}`;
  return (
    <div className={className} data-testid={id}>
      <Head title={debtTitle(d)} onRemove={onRemove} />
      <div className="two">
        <SelectField
          label="What is it"
          value={d.kind}
          options={DEBT_KIND_LABEL}
          onChange={(kind) => onChange({ kind })}
          testId={`${id}-kind`}
          style={fieldStyle}
        />
        <TextField
          label="Name (optional)"
          value={d.name}
          placeholder="e.g. TD line of credit"
          onChange={(name) => onChange({ name })}
          testId={`${id}-name`}
          style={fieldStyle}
        />
      </div>
      <div className="two" style={fieldStyle && { marginTop: 12 }}>
        <MoneyField
          label="Amount owing"
          value={d.balance}
          required
          onChange={(balance) => onChange({ balance })}
          error={err(`debts.${i}.balance`)}
          testId={`${id}-balance`}
          style={fieldStyle}
        />
        <RateField
          label="Interest rate"
          value={d.rate}
          required={false}
          placeholder={RATE_UNKNOWN}
          onChange={(rate) => onChange({ rate })}
          error={err(`debts.${i}.rate`)}
          testId={`${id}-rate`}
          style={fieldStyle}
        />
      </div>
    </div>
  );
}
